import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { DEVHOST, OptionType } from 'src/app/core/enums/enums';
import { Flight, Segment } from 'src/app/core/models/itinerary.model';
import { ContractError } from 'src/app/core/models/models';
import { ItineraryService } from '../../services/itinerary.service';
import { ReRouteService } from 'src/app/re-route/services/re-route.service';
import { CancellationService } from 'src/app/cancellation/services/cancellation.service';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { Subscription } from 'rxjs';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

@Component({
  selector: 'app-segments',
  templateUrl: './segments.component.html',
  styleUrls: ['./segments.component.scss']
})
export class SegmentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() segments: Segment[] = [];
  @Input() isTicketOpen = false;
  @Input() isReIssueFlow = false;
  @Input() baggageAllowance: number;
  @Input() endorsement: string = null;
  @Input() encryptedFare: string = null;
  @Input() selectedMode: OptionType;
  @Input() set restart(value: ContractError) {
    if (value) {
      this.selectAll(false);
      this.resetCanSelect();
    }
  }

  @Output() sendSelecteds = new EventEmitter<number[]>();
  @Output() loadAlternatives = new EventEmitter<boolean>();

  isDevelop: boolean = false;
  allSelected = false;
  isMultidestiny = true;
  totalFlights: number[];
  selectedFlights: number[][] = [];
  canSelect = false;
  mode: string;
  resetFlights: boolean;
  syncForm = false;
  receivedFromForm: UntypedFormGroup;
  isPreviousReceivedFrom = false;

  subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private itineraryService: ItineraryService,
    private reRouteService: ReRouteService,
    private cancellationService: CancellationService,
    private layoutService: LayoutService
  ) {}

  ngOnInit() {
    this.isDevelop = window.location.host.includes(DEVHOST.host);
    this.buildForm();
    this.isMultidestinyTrip();
    this.totalFlights = this.getIndexs();
    this.resetCanSelect();
    this.checkSelectMode(this.selectedMode);
    this.callComponentSubcriptions();
  }

  buildForm(): void {
    this.receivedFromForm = this.fb.group({
      receivedFrom: ['', Validators.required]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.checkSelectMode(this.selectedMode);
  }

  callComponentSubcriptions(): void {
    this.subscriptions.add(
      this.layoutService.getReceivedFrom().subscribe((res) => {
        if (res) {
          this.receivedFromForm.patchValue({
            receivedFrom: res
          });
          this.isPreviousReceivedFrom = true;
          this.receivedFromForm.controls['receivedFrom'].disable();
          this.saveReceivedFrom();
        } else {
          this.isPreviousReceivedFrom = false;
          this.receivedFromForm.controls['receivedFrom'].enable();
        }
      })
    );
  }

  checkSelectMode(value: OptionType) {
    this.selectAll(false);
    this.canSelect = !!value;
    this.mode = value;
    this.mode === OptionType.VoluntaryChange ? this.resetCanSelect(false) : this.resetCanSelect();

    if (
      value === OptionType.OpenTicket ||
      value === OptionType.ExchangeNoCost ||
      value === OptionType.VoluntaryChange
    ) {
      this.sendSelecteds.emit(this.totalFlights);
      this.canSelect = false;
    }
  }

  isMultidestinyTrip() {
    if (this.segments.length === 2 && this.isRoundTrip()) {
      this.isMultidestiny = false;
    }
  }

  isRoundTrip(): boolean {
    return this.segments[0].departureLocation.code === this.segments[1].arrivalLocation.code;
  }

  openSyncForm(segment: Segment) {
    this.syncForm = true;
    this.sendSelecteds.emit(this.getIndexs([segment]));
  }

  loadAlternativesInvoluntary(segment: Segment) {
    // this.sendSelecteds.emit(this.getIndexs([segment]));
    this.loadAlternatives.emit(true);
  }

  selectAll(value: boolean) {
    this.allSelected = value;
    this.segments.forEach((segment, index) => {
      segment.isChecked = value;
      let flights: number[];
      flights = [];
      segment.flights.forEach((flight) => {
        flight.isChecked = value;
        flights.push(flight.index);
      });
      this.selectedFlights[index] = flights;
    });
    this.sendSelectAll(value);
  }

  sendSelectAll(value: boolean) {
    if (value) {
      this.sendSelecteds.emit(this.totalFlights);
    } else {
      this.selectedFlights = [];
      this.sendSelecteds.emit([]);
    }
  }

  getIndexs(segments: Segment[] = this.segments): number[] {
    return this.getFlights(segments).reduce((acc, flight) => acc.concat(flight.index), []);
  }

  getFlights(segments: Segment[]): Flight[] {
    return segments.reduce((acc, segment) => acc.concat(segment.flights), []);
  }

  isAllSelected(selecteds: number[]): boolean {
    return this.totalFlights.length === selecteds.length;
  }

  isAllDeselected(selecteds: number[]): boolean {
    return selecteds.length === 0;
  }

  activeMode(i: number, type: string) {
    if (this.mode === OptionType.Cancellation) {
      switch (type) {
        case 'all':
          this.canSelectSegments(i, true);
          break;
        case 'some':
          this.canSelectSegments(i, false);
          break;
        case 'none':
          if (!this.allSelected) {
            this.resetCanSelect();
          }
          break;
        default:
          break;
      }
    }
  }

  selecteds(response: { indexs: number[]; type?: string }, i: number) {
    this.selectedFlights[i] = response.indexs;
    this.activeMode(i, response.type);
    const flat = this.selectedFlights.reduce((a, b) => a.concat(b), []);

    this.sendSelecteds.emit(flat);
    this.allSelected = this.isAllSelected(flat);
  }

  canSelectSegments(i: number, canSelect: boolean) {
    this.segments.forEach((segment, index) => {
      if (index !== i) {
        segment.canSelect = canSelect;
        segment.canSelectFlights = false;
      }
    });
  }

  resetCanSelect(canSelectFlights: boolean = true) {
    this.segments.forEach((segment) => {
      segment.canSelect = true;
      segment.canSelectFlights = canSelectFlights;
    });
  }

  selectText(e) {
    if (!window.getSelection) {
      return;
    }

    const node = e.target;
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  removeReceivedFrom() {
    this.layoutService.dispatchRemoveReceivedFrom();
    this.itineraryService.dispatchRemoveReceivedFrom();
    this.reRouteService.dispatchRemoveReceivedFrom();
    this.cancellationService.dispatchRemoveReceivedFrom();

    this.receivedFromForm.patchValue({
      receivedFrom: ''
    });
  }

  saveReceivedFrom() {
    const receivedFromValue: string = this.receivedFromForm.value?.receivedFrom;

    this.layoutService.dispatchSetReceivedFrom(receivedFromValue.toUpperCase());
    this.itineraryService.dispatchSetReceivedFrom(receivedFromValue.toUpperCase());
    this.reRouteService.dispatchSetReceivedFrom(receivedFromValue.toUpperCase());
    this.cancellationService.dispatchSetReceivedFrom(receivedFromValue.toUpperCase());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
